/**
 * Shared PostHog initialization state.
 * Components use `isPostHogReady()` instead of the private `posthog.__loaded`
 * to avoid depending on an undocumented SDK property.
 *
 * `onPostHogReady` handles the React bottom-up effect ordering race: child
 * components that call PostHog APIs mount before the root PostHogInit component
 * fires its effect. Registering a callback guarantees the call is deferred until
 * PostHog is actually initialized, even on the very first render.
 */
let posthogInitialized = false
const readyCallbacks: Array<() => void> = []

export function markPostHogInitialized(): void {
  posthogInitialized = true
  // Flush any components that registered before init completed
  readyCallbacks.splice(0).forEach((cb) => cb())
}

export function isPostHogReady(): boolean {
  return posthogInitialized
}

/**
 * Calls `cb` immediately if PostHog is already initialized, otherwise defers
 * until `markPostHogInitialized()` fires. Returns an unsubscribe function so
 * React effects can clean up the pending callback on unmount (important in
 * Strict Mode where effects double-fire).
 * @param cb - Callback to invoke once PostHog is ready.
 * @returns Cleanup function that removes the pending callback.
 */
export function onPostHogReady(cb: () => void): () => void {
  if (posthogInitialized) {
    // PostHog is already up — call cb() synchronously. This is intentional:
    // useEffect bodies run after paint so calling setState synchronously here
    // is safe and won't trigger a "can't update state on unmounted component"
    // warning. The caller still receives the cleanup function it needs to return.
    cb()
    return () => {}
  }
  readyCallbacks.push(cb)
  return () => {
    const i = readyCallbacks.indexOf(cb)
    if (i >= 0) readyCallbacks.splice(i, 1)
  }
}
